// ── Trip Pass — frontend mirror types ────────────────────────────────────────
//
// Mirrors `backend/src/config/trip-pass-config.ts`. Server-side Firestore
// `Timestamp` values arrive serialized as `{ _seconds, _nanoseconds }` (when
// admin SDK writes flow through API routes) or as ISO strings (when the API
// pre-serializes). We accept either to match the convention in
// `frontend/src/types/index.ts` (see e.g. `createdAt` on the history entry).
//
// Constants are re-declared here (rather than imported) because the frontend
// is a separate TS project and the backend's `process.env.FEATURE_TRIP_PASS`
// is resolved server-side. The frontend learns whether the feature is enabled
// via the public catalog / API.

// ── Public constants surfaced to UX ──────────────────────────────────────────

export const TRIP_PASS_PRICE_CENTS = 1900
export const TRIP_PASS_BASE_WINDOW_DAYS = 30
export const TRIP_PASS_END_DATE_BUFFER_DAYS = 3
export const TRIP_PASS_MAX_WINDOW_DAYS = 365
export const TRIP_PASS_REFUND_WINDOW_DAYS = 7
export const TRIP_PASS_PRO_CREDIT_WINDOW_DAYS = 15
export const TRIP_PASS_SLOTS_CONCURRENT = 3
export const TRIP_PASS_SLOTS_LIFETIME = 5

// ── One-time SKU ─────────────────────────────────────────────────────────────

export type OneTimeSku = 'trip_pass'

// ── Status state machine (mirrors backend) ───────────────────────────────────

export type TripPassStatus =
  | 'pending'
  | 'active'
  | 'exhausted'
  | 'expired'
  | 'refunded'

// ── Wire-format timestamp ────────────────────────────────────────────────────
//
// Matches the union already used in `frontend/src/types/index.ts` for
// Firestore-Timestamps that arrive via REST: either the JSON-serialized
// admin Timestamp shape, or an ISO string when the API normalizes.

export type WireTimestamp = { _seconds: number; _nanoseconds: number } | string

// ── TripPass shape (frontend view) ───────────────────────────────────────────

export interface TripPass {
  id: string
  uid: string
  status: TripPassStatus
  priceCents: number
  currency: string

  stripePaymentIntentId: string
  stripeCheckoutSessionId: string
  stripeCustomerId: string

  purchasedAt: WireTimestamp
  baseExpiresAt: WireTimestamp
  effectiveExpiresAt: WireTimestamp
  refundEligibleUntil: WireTimestamp
  proCreditEligibleUntil: WireTimestamp
  refundedAt?: WireTimestamp

  slotsTotal: number
  slotsLifetimeCap: number
  attachmentsLifetime: number
  activeTripRefs: Array<{
    groupId: string
    tripId: string
    attachedAt: WireTimestamp
  }>

  attributedFeature: string

  /** P3-2: groupId/tripId the user picked at checkout. Stamped before the
   *  auto-attach attempt so support can audit drop-offs even when the
   *  attach fails (e.g. trip deleted between Stripe redirect and webhook). */
  intendedAttach?: {
    groupId: string
    tripId: string
  }

  proCreditApplied: boolean
  proCreditAppliedAt?: WireTimestamp
  proCreditAppliedTo?: string

  createdAt: WireTimestamp
  updatedAt: WireTimestamp
}
